import { ApolloQueryResult, FetchResult, gql } from '@apollo/client';
import { apolloMutation, apolloQuery } from '../apollo';
import { CreateOrderMutation, NewOrder, OrderQuery, UpdateOrderItemsInput, UpdateOrderItemsMutation } from '../gql/graphql';

export interface Orders {
  getOrderById(id: string): Promise<ApolloQueryResult<OrderQuery>>;
  getOrderByIdWithCartValidation(id: string): Promise<ApolloQueryResult<OrderQuery>>;
  createOrder(input: NewOrder): Promise<FetchResult<CreateOrderMutation>>;
  updateOrderItems(input: UpdateOrderItemsInput): Promise<FetchResult<UpdateOrderItemsMutation>>;
}

const orderService: Orders = {
  async getOrderById(id: string) {
    return apolloQuery(ORDER_QUERY, { id: id }) as Promise<ApolloQueryResult<OrderQuery>>;
  },
  async getOrderByIdWithCartValidation(id: string) {
    return apolloQuery(ORDER_QUERY_WITH_CART_VALIDATION, { id: id }) as Promise<ApolloQueryResult<OrderQuery>>;
  },
  async createOrder(input: NewOrder) {
    return apolloMutation(createOrderMutation, { input: input }) as Promise<FetchResult<CreateOrderMutation>>;
  },
  async updateOrderItems(input: UpdateOrderItemsInput) {
    return apolloMutation(updateOrderItemsMutation, { input: input }) as Promise<FetchResult<UpdateOrderItemsMutation>>;
  },
};

export const ORDER_QUERY = gql`
  query order($id: ID!) {
    order(id: $id) {
      dealer {
        id
        integrationType
        financingType
        domains
        publicDealerId
        storeName
        friendlyName
        dealerImage
        isVirtualCard
        integrationDetails {
          integrationType
          platformName
        }
        ipBehavior
        formType
        channelType
        isAllowedToCreateNewApplications
        supportsExternalBankConnector
        companyDealerId
        pricingType
        isLTOInsurance
        dealerAddress {
          state
        }
        taxRate
      }
      application {
        loan {
          id
          summary {
            id
            finalPaymentAmount
            paymentAmount
            firstPaymentDue
            annualPercentageRate
            financeCharge
            termLength
            amountFinanced
            loanAmount
            deferredInterestPeriod
          }
          achDetails {
            routingNumber
            accountNumberLast4
          }
          status
          isOtb
          maxLoanAmount
          cartValidationDetails
          options {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
        }
        lease {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLeaseAmount
          neuroIdIdentityId
          ipUpperBoundary
          ipBehavior
          options {
            leaseTerm
            sacTerm
            earlyBuyoutDiscount
            approvedAmount
            applicationFee
            ipUpperBoundary
            minimumSpend
            authCapture
          }
          employment {
            payPeriod
            lastPayDate
            nextPayDate
            monthlyIncome
            isPaidViaDirectDeposit
          }
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
          bank {
            accountNumberLast4
            routingNumber
          }
          contractSummaries {
            leaseTerm
            leaseAmount
            cashPlusPrice
            cashPlusTerm
            cashPlusFee
            initialPaymentWithTax
            initialPaymentAmount
            paymentFrequency
            tax
            totalCostOfOwnership
            scheduledPaymentAmounts
          }
        }
        installmentLoan {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLoanAmount
          selectedOptionId
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
        }
        customer {
          id
          firstName
          lastName
          cellPhone
          emailAddress
          birthDate
          tokenizedTaxId
          protectionPlusStatus
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
      }
      details {
        customer {
          firstName
          lastName
          emailAddress
          cellPhone
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
        items {
          category
          displayName
          quantity
          sku
          price
          itemUrl
          itemImageUrl
          isLeaseable
        }
        shippingAmnt
        taxAmnt
      }
      metadata {
        performBAV
      }
    }
  }
`;

export const ORDER_QUERY_WITH_CART_VALIDATION = gql`
  query orderWithCV($id: ID!) {
    order(id: $id) {
      dealer {
        id
        integrationType
        financingType
        domains
        publicDealerId
        storeName
        friendlyName
        dealerImage
        isVirtualCard
        integrationDetails {
          integrationType
          platformName
        }
        ipBehavior
        formType
        channelType
        isAllowedToCreateNewApplications
        supportsExternalBankConnector
        companyDealerId
        pricingType
        isLTOInsurance
        dealerAddress {
          state
        }
        taxRate
      }
      application {
        loan {
          id
          summary {
            id
            finalPaymentAmount
            paymentAmount
            firstPaymentDue
            annualPercentageRate
            financeCharge
            termLength
            amountFinanced
            loanAmount
            deferredInterestPeriod
          }
          achDetails {
            routingNumber
            accountNumberLast4
          }
          status
          isOtb
          maxLoanAmount
          cartValidationDetails
          options {
            productID
            deferredInterestPeriod
            lineAmount
            merchantDiscountRate
            minimumSpend
            rate
            termLength
          }
        }
        lease {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLeaseAmount
          neuroIdIdentityId
          ipUpperBoundary
          ipBehavior
          cartValidationAdditionalDetails {
            displayText
            status
            data {
              approvalAmount
              cartTotal
              contractState
              minSpend
            }
            cartValidationType
            cartItemValidationBlock {
              displayText
              displayName
              id
              reasons
            }
            cartItemValidationWarn {
              displayText
              displayName
              id
              reasons
            }
          }
          options {
            leaseTerm
            sacTerm
            earlyBuyoutDiscount
            approvedAmount
            applicationFee
            ipUpperBoundary
            minimumSpend
            authCapture
          }
          employment {
            payPeriod
            lastPayDate
            nextPayDate
            monthlyIncome
            isPaidViaDirectDeposit
          }
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
          bank {
            accountNumberLast4
            routingNumber
          }
          contractSummaries {
            leaseTerm
            leaseAmount
            cashPlusPrice
            cashPlusTerm
            cashPlusFee
            initialPaymentWithTax
            initialPaymentAmount
            paymentFrequency
            tax
            totalCostOfOwnership
            scheduledPaymentAmounts
          }
        }
        installmentLoan {
          id
          status
          declineReason
          decisionCode
          displayId
          publicId
          draftStep
          appStep
          isOtb
          maxLoanAmount
          selectedOptionId
          card {
            isExpired
            provider
            lastFour
            expirationDate
          }
        }
        customer {
          id
          firstName
          lastName
          cellPhone
          emailAddress
          birthDate
          tokenizedTaxId
          protectionPlusStatus
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
      }
      details {
        customer {
          firstName
          lastName
          emailAddress
          cellPhone
          accommodationAddress {
            line1
            line2
            city
            state
            zip
          }
          billingAddress {
            line1
            line2
            city
            state
            zip
          }
          shippingAddress {
            line1
            line2
            city
            state
            zip
          }
        }
        items {
          category
          displayName
          quantity
          sku
          price
          itemUrl
          itemImageUrl
          isLeaseable
        }
        shippingAmnt
        taxAmnt
      }
      metadata {
        performBAV
      }
    }
  }
`;

const createOrderMutation = gql`
  mutation createOrder($input: NewOrder!) {
    createOrder(input: $input) {
      order {
        id
      }
      orderErrors {
        __typename
        ... on FatalError {
          message
        }
        ... on BadRequest {
          message
        }
      }
    }
  }
`;

const updateOrderItemsMutation = gql`
  mutation updateOrderItems($input: UpdateOrderItemsInput!) {
    updateOrderItems(input: $input) {
      order {
        details {
          items {
            category
            displayName
            quantity
            sku
            price
            itemUrl
            itemImageUrl
            isLeaseable
          }
          shippingAmnt
          taxAmnt
        }
      }
      cartItemValidationWarn {
        displayText
        displayName
        id
        reasons
      }
      orderErrors {
        __typename
        ... on NotFound {
          message
          path
        }
        ... on FatalError {
          message
          path
        }
        ... on BadRequest {
          message
          path
        }
        ... on ServiceFeesTooHigh {
          message
          path
        }
        ... on CartMinNotMet {
          message
          path
        }
        ... on CartValidation {
          message
          path
          extensions {
            validationErrType
            approvalType
            approvedAmount
            minimumSpend
            cartTotal
            contractState
            cartItemValidationBlock {
              displayText
              displayName
              reasons
              id
            }
            displayText
            exceededCapDetails {
              cap {
                name
                cap
                percentage
              }
              items {
                displayText
                displayName
                reasons
                id
              }
            }
          }
        }
      }
      validationErrors {
        __typename
        ... on CartValidation {
          message
          extensions {
            validationErrType
            approvalType
            approvedAmount
            minimumSpend
            cartTotal
            contractState
            cartItemValidationBlock {
              displayText
              displayName
              reasons
            }
            displayText
            exceededCapDetails {
              cap {
                name
                cap
                percentage
              }
              items {
                displayText
                displayName
                reasons
                id
              }
            }
          }
        }
        ... on ServiceFeesTooHigh {
          message
        }
        ... on CartMinNotMet {
          message
        }
      }
    }
  }
`;

export default orderService;
